/**
 * Admin panel route group — owned by the "admin panel" worker.
 *
 * `App.tsx` mounts this at `/admin/*` (behind `ProtectedRoute role="admin"`),
 * so every sub-route lives here rather than in the shared route map.
 *
 *   /admin                    dashboard
 *   /admin/live               live monitor — every running attempt
 *   /admin/exams              exam list
 *   /admin/exams/new          exam builder
 *   /admin/exams/:id          exam builder, existing paper
 *   /admin/analysis           item analysis — what the responses say about
 *                             the QUESTIONS
 *   /admin/questions          question bank
 *   /admin/questions/review   review queue (keyboard-driven)
 *   /admin/attempts           attempt list
 *   /admin/attempts/:id       attempt review — video, timeline, grading
 *   /admin/students           the roster — add, edit, deactivate
 *   /admin/students/:id       progress over time
 */
import { createContext, useContext } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { AdminNav, AdminStyles, ErrorNote, Loading } from "./components/Shell";
import { fetchCatalog } from "./data/client";
import type { Catalog } from "./data/types";
import { useAsync } from "./lib/useAsync";
import { DashboardPage } from "./DashboardPage";
import { ExamsPage } from "./ExamsPage";
import { ExamBuilderPage } from "./ExamBuilderPage";
import { QuestionsPage } from "./QuestionsPage";
import { ReviewQueuePage } from "./ReviewQueuePage";
import { AttemptsPage } from "./AttemptsPage";
import { AttemptReviewPage } from "./AttemptReviewPage";
import { StudentPage } from "./StudentPage";
import { StudentsPage } from "./StudentsPage";
import { LiveMonitorPage } from "./LiveMonitorPage";
import { ItemAnalysisPage } from "./ItemAnalysisPage";

/**
 * Subjects and topics are needed by nearly every screen and change about once a
 * semester, so they are fetched once here and shared rather than refetched per
 * route.
 */
const CatalogContext = createContext<Catalog | null>(null);

export function useCatalog(): Catalog {
  const catalog = useContext(CatalogContext);
  if (!catalog) throw new Error("useCatalog must be used inside <AdminHome>");
  return catalog;
}

export function AdminHome() {
  const catalog = useAsync(() => fetchCatalog(), []);

  return (
    <AppShell title="Admin" actions={<AdminNav />}>
      <AdminStyles />
      {catalog.loading ? (
        <Loading />
      ) : catalog.error ? (
        <ErrorNote error={catalog.error} />
      ) : catalog.data ? (
        <CatalogContext.Provider value={catalog.data}>
          <Routes>
            <Route index element={<DashboardPage />} />
            <Route path="live" element={<LiveMonitorPage />} />
            <Route path="analysis" element={<ItemAnalysisPage />} />
            <Route path="exams" element={<ExamsPage />} />
            <Route path="exams/new" element={<ExamBuilderPage />} />
            <Route path="exams/:examId" element={<ExamBuilderPage />} />
            <Route path="questions" element={<QuestionsPage />} />
            <Route path="questions/review" element={<ReviewQueuePage />} />
            <Route path="attempts" element={<AttemptsPage />} />
            <Route path="attempts/:attemptId" element={<AttemptReviewPage />} />
            <Route path="students" element={<StudentsPage />} />
            <Route path="students/:studentId" element={<StudentPage />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </CatalogContext.Provider>
      ) : null}
    </AppShell>
  );
}
